import React, { useEffect, useState } from "react";
import { useQueryClient } from "react-query";

import styled from "styled-components";

import HtmlHead from "components/atoms/HtmlHead";
import Icon from "components/atoms/Icon";
import Alert from "components/atoms/alert/Alert";
import Confirm from "components/atoms/alert/Confirm";
import Button from "components/atoms/button/Button";
import StyledText from "components/atoms/text/StyledText";

import BodyWithBreadcrumb from "components/molecules/BodyWithBreadcrumb";
import Breadcrumb from "components/molecules/Breadcrumb";
import AbstractModal from "components/page/modal/AbstractModal";
import PaymentBillingHistoryModal from "components/page/modal/PaymentBillingHistoryModal";
import PaymentExtendSubscribeModal from "components/page/modal/PaymentExtendSubscribeModal";
import PaymentGroupMemberModal from "components/page/modal/PaymentGroupMemberModal";
import PaymentGroupRenameModal from "components/page/modal/PaymentGroupRenameModal";
import PaymentSubscribeModal from "components/page/modal/PaymentSubscribeModal";
import LoginUserLayout from "components/templates/LoginUserLayout";

import {
    usePaymentBillingHistory,
    usePaymentBillingKeyCreate,
    usePaymentBillingKeys,
    usePaymentCoupon,
    usePaymentDiscountPolicy,
    usePaymentPlanGroup,
    usePaymentPlanGroupUserDelete,
    usePaymentPlanGroupUserUpdate,
    usePaymentPlanSubscriptionHistory,
    usePaymentPlanSubscriptionHistoryCreate,
    usePaymentPlanSubscriptionHistoryUpdate,
} from "hooks/queries/usePayment";
import { useUser } from "hooks/queries/useUser";

import Constants from "utils/constants";
import { dateKr } from "utils/dateUtil";

import moment from "moment/moment";

const PageTitle = styled(StyledText).attrs({
    fontSize: "large",
    fontWeight: "bold",
})``;

const ContentsWrap = styled.div`
    padding: 40px 0 160px 0;
    width: 100%;
    max-width: 1080px;
`;

const Section = styled.div`
    &:not(:first-of-type) {
        margin-top: 20px;
    }
`;

const SectionTitle = styled(StyledText).attrs({
    withBarType: true,
    fontSize: "regular",
    fontWeight: "bold",
    style: { display: "flex", alignItems: "center", gap: "8px" },
})``;

const SectionContents = styled.div`
    padding: 20px 10px;
`;

const SectionContentsListItem = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin: 0;
    height: 32px;
    width: 100%;
`;

const SectionContentsListItemText = styled(StyledText).attrs({
    fontSize: "regular",
    fontWeight: "regular",
    color: "var(--color-Grey1)",
})``;

const DivideSection = styled.div`
    margin: 40px 0;
    width: 100%;
    height: 1px;
    border-bottom: solid 1px var(--color-Line);
`;

const TableWrap = styled.div`
    margin: 8px 0 0 0;
    width: 100%;
    border: solid 1px var(--color-Outline);
    border-radius: 4px;
    overflow: hidden;
`;

const Table = styled.table`
    width: 100%;
    padding: 0 0 160px 0;
`;

const Tr = styled.tr`
    border-bottom: solid 1px var(--color-Outline);
    overflow: hidden;

    &:last-child {
        border-bottom: transparent;
    }
`;

const Th = styled.th`
    padding: 12px 16px;
    width: ${(props) => props.width};
    background-color: var(--color-Base1);
    text-align: ${(props) => props.textAlign || "center"};
    border-right: solid 1px var(--color-Grey3);

    &:last-child {
        border-right: transparent;
    }
`;

const Td = styled.td`
    padding: ${(props) => props.padding || "12px 16px"};
    background-color: var(--color-White);
    text-align: ${(props) => props.textAlign || "center"};
    border-right: solid 1px var(--color-Grey3);

    &:last-child {
        border-right: transparent;
    }
`;

const ThText = styled(StyledText).attrs({
    fontSize: "regular",
    color: "var(--color-Black)",
})``;

const TdText = styled(StyledText).attrs({
    fontSize: "regular",
    color: "var(--color-SubBlack)",
})``;

const Amount = styled.span`
    margin: 0 4px 0 0;
`;

const DetailRow = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const SubscribePlanWrapper = styled.div`
    border: 1px solid var(--color-Background);
    border-radius: 4px;
    padding: 16px;
    background-color: var(--color-White);
`;

const SubscribePlanTitle = styled(StyledText)`
    display: flex;
    align-items: center;
    font-size: 1.6rem;
    font-weight: 500;
    margin-bottom: 12px;
`;

const SubscribePlanInfoBox = styled.div`
    background-color: var(--color-InfoBox);
    padding: 16px;
    border-radius: 4px;
`;

const SubscribePlanSubTitle = styled(StyledText)`
    font-size: 1.4rem;
    font-weight: 400;
    margin-bottom: 12px;
`;

const TitleRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const SubscribePlanFeature = styled(StyledText)`
    font-size: 1.4rem;
    font-weight: 400;
    color: var(--color-SubBlack);
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const GroupPlanFeature = styled(SubscribePlanFeature)`
    margin-top: 12px;
    display: flex;
    align-items: center;
`;

const GroupPlanFeatureUnderlineText = styled.span`
    margin-left: 8px;
    cursor: pointer;
    text-decoration: underline;
    width: auto;
    color: var(--color-Grey1);
`;

const GroupAdminBadge = styled(StyledText)`
    font-size: 1.2rem;
    font-weight: 500;
    color: white;
    background-color: var(--color-Button1);
    border-radius: 4px;
    padding: 2px 6px;
    margin-left: 8px;
    width: 50px;
    text-align: center;
`;

const SubscribePlanCouponText = styled(StyledText)`
    font-size: 1.4rem;
    font-weight: 400;
    color: var(--color-Key);
    display: flex;
    gap: 6px;
`;

const SubscribePlanFooterText = styled(StyledText)`
    margin-top: 16px;
    font-size: 1.2rem;
    color: var(--color-Grey1);
`;

const CouponRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: start;
    gap: 16px;
    margin-top: 16px;
`;

const GroupTitleSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
`;

const GroupTitle = styled.h2`
    font-size: 1.4rem;
    font-weight: 400;
`;

const GroupSubscribePlanInfoBox = styled(SubscribePlanInfoBox)`
    padding: 8px 16px;
`;

const GroupDetailList = styled.ul`
    list-style: none;
    padding: 0;
`;

const GroupDetailItem = styled.li`
    margin: 10px 0;
    font-size: 12px;
    font-weight: 400;
    color: #555;
`;

const GroupItemLabel = styled.span`
    font-size: 1.4rem;
    font-weight: 400;
`;

const GroupItemValue = styled.span`
    margin-left: 8px;
    font-size: 1.4rem;
`;

const NoticeSection = styled.section`
    margin-top: 10px;
    font-size: 1.2rem;
    color: #666;
`;

const NoticeList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const NoticeItem = styled.li`
    &:before {
        content: "•";
        margin-right: 8px;
        color: #666;
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 8px;
`;

function PaymentPage() {
    const QueryClient = useQueryClient();

    const [user, setUser] = useState({});
    const [subscribeHistory, setSubscribeHistory] = useState([]);
    const [billingKey, setBillingKey] = useState([]);
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [paymentPlanGroup, setPaymentPlanGroup] = useState([]);
    const [policy, setPolicy] = useState({});

    const [showPlanGroupMemberModal, setShowPlanGroupMemberModal] = useState(false);
    const [showPlanGroupRenameModal, setShowPlanGroupRenameModal] = useState(false);
    const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
    const [showPaidPlanSubscribeModal, setShowPaidPlanSubscribeModal] = useState(false);
    const [showExtendSubscribeModal, setShowExtendSubscribeModal] = useState(false);

    const userQuery = useUser();
    const billingKeyQuery = usePaymentBillingKeys();
    const subscribeHistoryQuery = usePaymentPlanSubscriptionHistory();
    const paymentHistoryQuery = usePaymentBillingHistory();
    const discountPolicyQuery = usePaymentDiscountPolicy();

    const subscribeHistoryUpdateMutation = usePaymentPlanSubscriptionHistoryUpdate();

    const paymentPlanGroupQuery = usePaymentPlanGroup();

    const couponQuery = usePaymentCoupon();
    const [couponList, setCouponList] = useState([]);

    const paymentPlanGroupUserUpdateMutation = usePaymentPlanGroupUserUpdate();
    const paymentPlanGroupUserDeleteMutation = usePaymentPlanGroupUserDelete();

    useEffect(() => {
        const jquery = document.createElement("script");
        jquery.src = "https://code.jquery.com/jquery-1.12.4.min.js";
        const iamport = document.createElement("script");
        iamport.src = "https://cdn.iamport.kr/js/iamport.payment-1.1.8.js";
        document.head.appendChild(jquery);
        document.head.appendChild(iamport);
        return () => {
            document.head.removeChild(jquery);
            document.head.removeChild(iamport);
        };
    }, []);

    useEffect(() => {
        if (billingKeyQuery.data) {
            setBillingKey(billingKeyQuery.data);
        }
        if (userQuery.data) {
            setUser(userQuery.data);
        }

        if (subscribeHistoryQuery.data) {
            const activeHistory = subscribeHistoryQuery.data.filter((subscribe) => subscribe.status === "ACTIVE");
            setSubscribeHistory(activeHistory);
        }

        if (paymentHistoryQuery.data) {
            setPaymentHistory(paymentHistoryQuery.data);
        }
        if (couponQuery.data) {
            setCouponList(couponQuery.data.filter((coupon) => !coupon.used_at));
        }
        if (discountPolicyQuery.data) {
            setPolicy(
                discountPolicyQuery.data.find((policy) => policy.internal_code === "THREE_MONTH_DISCOUNT_POLICY"),
            );
        }
    }, [
        billingKeyQuery.data,
        userQuery.data,
        subscribeHistoryQuery.data,
        paymentHistoryQuery.data,
        user.id,
        couponQuery.data,
        discountPolicyQuery.data,
    ]);

    useEffect(() => {
        setPaymentPlanGroup(paymentPlanGroupQuery.data);
    }, [paymentPlanGroupQuery.data]);

    const showDiscountRate = (discountPolicy) => {
        let totalRate = 0;
        if (discountPolicy && discountPolicyQuery.data) {
            discountPolicy.forEach((policyId) => {
                const discountRate = discountPolicyQuery.data.find((policy) => policy.id === policyId);
                if (discountRate) {
                    totalRate += Number(discountRate.discount_rate);
                }
            });
        }
        return totalRate > 0 ? `(${totalRate}% 할인)` : "";
    };

    return (
        <LoginUserLayout>
            <HtmlHead title={"결제 정보"} />
            <Breadcrumb>
                <PageTitle>Payment</PageTitle>
            </Breadcrumb>
            <BodyWithBreadcrumb>
                <ContentsWrap>
                    {/* 구독중인 요금제 */}
                    <Section>
                        <SectionTitle>이용 현황</SectionTitle>
                        {subscribeHistory.length === 0 ? (
                            <SectionContents>
                                <SubscribePlanWrapper>
                                    <SubscribePlanTitle>Basic</SubscribePlanTitle>
                                    <SubscribePlanInfoBox>
                                        <SubscribePlanSubTitle>Pro 요금제로 업그레이드하세요</SubscribePlanSubTitle>
                                        <SubscribePlanFeature>
                                            <Icon name={"storage"} size={16} />
                                            20GB의 확장된 저장공간
                                        </SubscribePlanFeature>
                                        <SubscribePlanFeature>
                                            <Icon name={"check"} size={16} />
                                            PDF 요약, 비평, 글쓰기 지원 도구 등 고급 연구 기능 사용
                                        </SubscribePlanFeature>
                                        <CouponRow>
                                            <Button
                                                onlyText
                                                width={"100px"}
                                                height={"32px"}
                                                margin={"0 0 0 0"}
                                                fontColor={"var(--color-White)"}
                                                bgColor={"var(--color-Button2)"}
                                                hoverBgColor={"var(--color-ButtonHover2)"}
                                                buttonText={"업그레이드"}
                                                onClick={(e) => setShowPaidPlanSubscribeModal(true)}
                                            />
                                            {couponList.length > 0 && (
                                                <SubscribePlanCouponText>
                                                    <Icon name="coupon" size={16} color="var(--color-Key)" />
                                                    이용 가능한 쿠폰이 있습니다.
                                                </SubscribePlanCouponText>
                                            )}
                                        </CouponRow>
                                    </SubscribePlanInfoBox>
                                    {subscribeHistory.filter((history) => history.plan === Constants.GROUP_PLAN)
                                        .length === 0 && (
                                        <SubscribePlanFooterText>
                                            2인 이상의 Group을 위한 <a href="/plan">요금제 확인</a>
                                        </SubscribePlanFooterText>
                                    )}
                                </SubscribePlanWrapper>
                            </SectionContents>
                        ) : (
                            subscribeHistory.map((history) => {
                                const isGroupPlan = history.plan === Constants.GROUP_PLAN;
                                const isGroupPlanUserType = history.group
                                    ? history.group.admin === user.id
                                        ? "admin"
                                        : "user"
                                    : null;

                                return (
                                    <SectionContents key={history.id}>
                                        <SubscribePlanWrapper>
                                            <SubscribePlanTitle>
                                                {history.plan_name}{" "}
                                                {isGroupPlanUserType === "admin" && (
                                                    <GroupAdminBadge>관리자</GroupAdminBadge>
                                                )}
                                            </SubscribePlanTitle>
                                            <SubscribePlanFeature>
                                                이용기간 : {dateKr(moment(history.started_at))}~{" "}
                                                {dateKr(moment(history.ended_at).add(-1, "days"))}
                                                {!isGroupPlan && (
                                                    <SectionContentsListItemText
                                                        style={{
                                                            cursor: "pointer",
                                                            textDecoration: "underline",
                                                            width: "auto",
                                                        }}
                                                        onClick={(e) => {
                                                            setShowExtendSubscribeModal({
                                                                is_only_coupon_use: true,
                                                                ...history,
                                                            });
                                                        }}
                                                    >
                                                        쿠폰 사용
                                                    </SectionContentsListItemText>
                                                )}
                                            </SubscribePlanFeature>
                                            {isGroupPlan && (
                                                <>
                                                    <GroupPlanFeature>
                                                        이름 : {history.group?.name}
                                                        {isGroupPlanUserType === "admin" && (
                                                            <GroupPlanFeatureUnderlineText
                                                                onClick={(e) => {
                                                                    setShowPlanGroupRenameModal(history.group);
                                                                }}
                                                            >
                                                                수정
                                                            </GroupPlanFeatureUnderlineText>
                                                        )}
                                                    </GroupPlanFeature>
                                                    <GroupPlanFeature>
                                                        {isGroupPlanUserType === "admin" ? (
                                                            <>
                                                                멤버 : 현재 {history.group?.active_users}명/ 최대{" "}
                                                                {history.group?.max_member}명{" "}
                                                                <GroupPlanFeatureUnderlineText
                                                                    onClick={(e) => {
                                                                        setShowPlanGroupMemberModal(history.group);
                                                                    }}
                                                                >
                                                                    관리
                                                                </GroupPlanFeatureUnderlineText>
                                                            </>
                                                        ) : (
                                                            <>
                                                                관리자 : {history.group?.admin_email}
                                                                <br />
                                                                <div
                                                                    style={{
                                                                        cursor: "pointer",
                                                                        textDecoration: "underline",
                                                                    }}
                                                                    onClick={(e) => {
                                                                        Confirm(
                                                                            "warn",
                                                                            "알림",
                                                                            "Group에서 나가겠습니까? \nGroup 요금제가 비활성화됩니다.",
                                                                            "나가기",
                                                                            () => {
                                                                                paymentPlanGroupUserDeleteMutation.mutate(
                                                                                    {
                                                                                        group_id: history.group.id,
                                                                                        id: history.group.group_user_id,
                                                                                        is_self_leaved: true,
                                                                                    },
                                                                                    {
                                                                                        onSuccess: () => {
                                                                                            Alert(
                                                                                                "info",
                                                                                                "구독",
                                                                                                "Group에서 나갔습니다.",
                                                                                            );
                                                                                        },
                                                                                    },
                                                                                );
                                                                            },
                                                                        );
                                                                    }}
                                                                >
                                                                    나가기
                                                                </div>
                                                            </>
                                                        )}
                                                    </GroupPlanFeature>
                                                </>
                                            )}
                                            {((isGroupPlan && isGroupPlanUserType === "admin") || !isGroupPlan) && (
                                                <SubscribePlanInfoBox>
                                                    {history.is_scheduled ? (
                                                        <>
                                                            {" "}
                                                            <TitleRow>
                                                                <SubscribePlanSubTitle>
                                                                    다음 자동 연장
                                                                </SubscribePlanSubTitle>
                                                                <SectionContentsListItem
                                                                    style={{
                                                                        cursor: "pointer",
                                                                        textDecoration: "underline",
                                                                        width: "auto",
                                                                    }}
                                                                >
                                                                    <SectionContentsListItemText
                                                                        onClick={(e) => {
                                                                            setShowPaymentHistoryModal("bill");
                                                                        }}
                                                                    >
                                                                        청구서
                                                                    </SectionContentsListItemText>
                                                                </SectionContentsListItem>
                                                            </TitleRow>
                                                            <SectionContentsListItem>
                                                                <SectionContentsListItemText>
                                                                    - 이용 기간 : {dateKr(moment(history.ended_at))} ~{" "}
                                                                    {history?.scheduled_billing_history?.discount_policy.includes(
                                                                        policy?.id,
                                                                    )
                                                                        ? dateKr(
                                                                              moment(history.ended_at)
                                                                                  .add(3, "months")
                                                                                  .add(-1, "days"),
                                                                          )
                                                                        : dateKr(
                                                                              moment(history.ended_at)
                                                                                  .add(1, "months")
                                                                                  .add(-1, "days"),
                                                                          )}
                                                                </SectionContentsListItemText>
                                                            </SectionContentsListItem>
                                                            <SectionContentsListItem>
                                                                <SectionContentsListItemText>
                                                                    - 결제 예정일 : {dateKr(history.ended_at)}
                                                                </SectionContentsListItemText>
                                                            </SectionContentsListItem>
                                                            <SectionContentsListItem>
                                                                <SectionContentsListItemText>
                                                                    - 결제수단 :{" "}
                                                                    {billingKey.find(
                                                                        (key) =>
                                                                            key.id ===
                                                                            history.scheduled_billing_history
                                                                                .billing_key,
                                                                    )?.card_name || ""}{" "}
                                                                    {billingKey.find(
                                                                        (key) =>
                                                                            key.id ===
                                                                            history.scheduled_billing_history
                                                                                .billing_key,
                                                                    )?.card_number || ""}
                                                                </SectionContentsListItemText>
                                                            </SectionContentsListItem>
                                                            <SectionContentsListItem>
                                                                <SectionContentsListItemText>
                                                                    - 결제 금액 :{" "}
                                                                    {history.scheduled_billing_history.amount.toLocaleString()}
                                                                    원
                                                                </SectionContentsListItemText>
                                                            </SectionContentsListItem>
                                                            <SectionContentsListItem
                                                                style={{
                                                                    cursor: "pointer",
                                                                    textDecoration: "underline",
                                                                }}
                                                            >
                                                                <SectionContentsListItemText
                                                                    onClick={(e) => {
                                                                        Confirm(
                                                                            "warn",
                                                                            "알림",
                                                                            "자동 연장을 해지하겠습니까? \n이용 기간이 만료되면 요금제가 구독 종료됩니다.",
                                                                            "해지",
                                                                            () => {
                                                                                subscribeHistoryUpdateMutation.mutate(
                                                                                    {
                                                                                        id: history.id,
                                                                                        is_scheduled: false,
                                                                                    },
                                                                                    {
                                                                                        onSuccess: () => {
                                                                                            Alert(
                                                                                                "info",
                                                                                                "구독",
                                                                                                "자동 연장이 해지되었습니다.",
                                                                                            );
                                                                                            subscribeHistoryQuery.refetch();
                                                                                        },
                                                                                    },
                                                                                );
                                                                            },
                                                                        );
                                                                    }}
                                                                >
                                                                    자동 연장 해지
                                                                </SectionContentsListItemText>
                                                            </SectionContentsListItem>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <SubscribePlanSubTitle>
                                                                {history.plan_name} 요금제의 이용기간을 연장하세요.
                                                            </SubscribePlanSubTitle>
                                                            <SubscribePlanFeature>
                                                                {dateKr(history.ended_at)}에 구독 종료됩니다.
                                                            </SubscribePlanFeature>
                                                            <CouponRow>
                                                                <Button
                                                                    onlyText
                                                                    width={"100px"}
                                                                    height={"32px"}
                                                                    margin={"0 0 0 0"}
                                                                    fontColor={"var(--color-White)"}
                                                                    bgColor={"var(--color-Button2)"}
                                                                    hoverBgColor={"var(--color-ButtonHover2)"}
                                                                    buttonText={"연장하기"}
                                                                    onClick={(e) =>
                                                                        setShowExtendSubscribeModal(history)
                                                                    }
                                                                />
                                                                {couponList.length > 0 &&
                                                                    history.plan !== Constants.GROUP_PLAN && (
                                                                        <SubscribePlanCouponText>
                                                                            <Icon
                                                                                name="coupon"
                                                                                size={16}
                                                                                color="var(--color-Key)"
                                                                            />
                                                                            이용 가능한 쿠폰이 있습니다.
                                                                        </SubscribePlanCouponText>
                                                                    )}
                                                            </CouponRow>
                                                        </>
                                                    )}
                                                </SubscribePlanInfoBox>
                                            )}
                                            {subscribeHistory.filter((history) => history.plan === Constants.GROUP_PLAN)
                                                .length === 0 && (
                                                <SubscribePlanFooterText>
                                                    2인 이상의 Group을 위한 <a href="/plan">요금제 확인</a>
                                                </SubscribePlanFooterText>
                                            )}
                                        </SubscribePlanWrapper>
                                    </SectionContents>
                                );
                            })
                        )}
                    </Section>

                    {paymentPlanGroup
                        ?.filter((group) => group.invitation_status)
                        ?.map((group) => (
                            <SectionContents>
                                <SubscribePlanWrapper>
                                    <GroupTitleSection>
                                        <GroupTitle>Group에 초대되었습니다.</GroupTitle>
                                        <ButtonGroup>
                                            <Button
                                                onlyText
                                                width={"100px"}
                                                height={"32px"}
                                                fontColor={"var(--color-White)"}
                                                bgColor={"var(--color-Button2)"}
                                                hoverBgColor={"var(--color-ButtonHover2)"}
                                                buttonText={"거절하기"}
                                                onClick={(e) => {
                                                    Confirm("warn", "알림", "초대를 거절하시겠습니까?", "거절", () => {
                                                        paymentPlanGroupUserUpdateMutation.mutate({
                                                            group_id: group.id,
                                                            type: "reject",
                                                        });
                                                    });
                                                }}
                                            />
                                            <Button
                                                onlyText
                                                width={"100px"}
                                                height={"32px"}
                                                fontColor={"var(--color-White)"}
                                                bgColor={"var(--color-Button2)"}
                                                hoverBgColor={"var(--color-ButtonHover2)"}
                                                buttonText={"수락하기"}
                                                onClick={(e) =>
                                                    Confirm("warn", "알림", "초대를 수락하시겠습니까?", "수락", () => {
                                                        paymentPlanGroupUserUpdateMutation.mutate(
                                                            {
                                                                group_id: group.id,
                                                                type: "accept",
                                                            },
                                                            {
                                                                onSuccess: () => {
                                                                    Alert(
                                                                        "info",
                                                                        "구독",
                                                                        "Group 요금제가 활성화되었습니다.",
                                                                    );
                                                                },
                                                                onError: (error) => {
                                                                    QueryClient.invalidateQueries(
                                                                        "paymentPlanSubscriptionHistory",
                                                                    );
                                                                    QueryClient.invalidateQueries("paymentPlanGroup");
                                                                    QueryClient.invalidateQueries(
                                                                        "paymentPlanGroupUser",
                                                                    );

                                                                    if (error.response.data.email) {
                                                                        Alert(
                                                                            "warn",
                                                                            "에러",
                                                                            error.response.data.email,
                                                                        );
                                                                    } else if (error.response.data.error) {
                                                                        Alert(
                                                                            "warn",
                                                                            "에러",
                                                                            error.response.data.error,
                                                                        );
                                                                    } else {
                                                                        Alert(
                                                                            "warn",
                                                                            "에러",
                                                                            "서버에 오류가 발생하였습니다.",
                                                                        );
                                                                    }
                                                                },
                                                            },
                                                        );
                                                    })
                                                }
                                            />
                                        </ButtonGroup>
                                    </GroupTitleSection>
                                    <GroupSubscribePlanInfoBox>
                                        <GroupDetailList>
                                            <GroupDetailItem>
                                                <GroupItemLabel>이름 :</GroupItemLabel>
                                                <GroupItemValue>{group.name}</GroupItemValue>
                                            </GroupDetailItem>
                                            <GroupDetailItem>
                                                <GroupItemLabel>관리자 :</GroupItemLabel>
                                                <GroupItemValue>{group.admin_email}</GroupItemValue>
                                            </GroupDetailItem>
                                        </GroupDetailList>
                                    </GroupSubscribePlanInfoBox>
                                    <NoticeSection>
                                        <NoticeList>
                                            <NoticeItem>
                                                초대를 수락하면 Group에 가입하여 모든 유료 기능을 사용할 수 있습니다.
                                            </NoticeItem>
                                        </NoticeList>
                                    </NoticeSection>
                                </SubscribePlanWrapper>
                            </SectionContents>
                        ))}
                    <DivideSection />

                    {/* 결제 내역 */}
                    <Section>
                        <SectionTitle>이용 내역</SectionTitle>
                        <SectionContents>
                            <TableWrap>
                                <Table>
                                    <tbody>
                                        <Tr>
                                            <Th width="160px">
                                                <ThText>일시</ThText>
                                            </Th>
                                            <Th textAlign="left">
                                                <ThText>상세</ThText>
                                            </Th>
                                            <Th width="120px">
                                                <ThText>영수증</ThText>
                                            </Th>
                                            <Th width="120px">
                                                <ThText>금액</ThText>
                                            </Th>
                                        </Tr>
                                        {paymentHistory.length === 0 ? (
                                            <Tr style={{ height: "80px" }}>
                                                <Td colSpan="4">
                                                    <TdText>이용 내역이 없습니다.</TdText>
                                                </Td>
                                            </Tr>
                                        ) : (
                                            paymentHistory.map((history, index) => (
                                                <Tr key={index}>
                                                    <Td>
                                                        <TdText>
                                                            {moment(history.created_at).format("YYYY.MM.DD HH:mm")}
                                                        </TdText>
                                                    </Td>
                                                    <Td textAlign="left">
                                                        <TdText>
                                                            {history.description}{" "}
                                                            {showDiscountRate(history?.discount_policy)}
                                                        </TdText>

                                                        {history.started_at && history.ended_at && (
                                                            <TdText style={{ marginTop: "4px" }}>
                                                                이용기간 : {dateKr(moment(history.started_at))} ~{" "}
                                                                {dateKr(moment(history.ended_at))}
                                                            </TdText>
                                                        )}
                                                    </Td>
                                                    <Td>
                                                        <DetailRow>
                                                            {history.amount > 0 && history.status === "COMPLETED" && (
                                                                <Button
                                                                    onlyText
                                                                    width={"60px"}
                                                                    height={"24px"}
                                                                    fontColor={"var(--color-White)"}
                                                                    size={"xsmall"}
                                                                    buttonText={"영수증"}
                                                                    bgColor={"var(--color-Button2)"}
                                                                    hoverBgColor={"var(--color-ButtonHover2)"}
                                                                    onClick={(e) => {
                                                                        setShowPaymentHistoryModal(history.id);
                                                                    }}
                                                                />
                                                            )}
                                                        </DetailRow>
                                                    </Td>
                                                    <Td textAlign="right" padding="0 24px 0 0">
                                                        <TdText>
                                                            {history.amount !== null && (
                                                                <>
                                                                    <Amount>{history.amount}</Amount>원
                                                                </>
                                                            )}
                                                        </TdText>
                                                    </Td>
                                                </Tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </TableWrap>
                        </SectionContents>
                    </Section>
                    <div
                        style={{
                            display: "none",
                            overFlow: "scroll",
                            position: "fixed",
                            zIndex: 10000,
                            top: 0,
                            left: 0,
                            width: "100vw",
                            height: "100vh",
                            padding: "120px 0",
                            background: "rgba(0,0,0,.48)",
                            boxSizing: "border-box",
                        }}
                        id={"iamport-modal"}
                    ></div>
                    {showPaymentHistoryModal && (
                        <AbstractModal
                            modalTitle=""
                            width={500}
                            height={640}
                            exitModal={(e) => setShowPaymentHistoryModal(null)}
                        >
                            <PaymentBillingHistoryModal id={showPaymentHistoryModal} user={user} />
                        </AbstractModal>
                    )}

                    {showPaidPlanSubscribeModal && (
                        <AbstractModal
                            modalTitle="요금제 결제"
                            width={500}
                            height={640}
                            exitModal={(e) => setShowPaidPlanSubscribeModal(!showPaidPlanSubscribeModal)}
                        >
                            <PaymentSubscribeModal
                                // plan={}
                                user={user}
                                exitModal={(e) => setShowPaidPlanSubscribeModal(!showPaidPlanSubscribeModal)}
                            />
                        </AbstractModal>
                    )}
                    {showExtendSubscribeModal && (
                        <AbstractModal
                            modalTitle="이용 기간 연장"
                            width={500}
                            height={640}
                            exitModal={(e) => setShowExtendSubscribeModal(false)}
                        >
                            <PaymentExtendSubscribeModal
                                user={user}
                                isGroupPlan={showExtendSubscribeModal.plan === Constants.GROUP_PLAN}
                                groupInfo={showExtendSubscribeModal.group}
                                subscription={showExtendSubscribeModal}
                                exitModal={(e) => setShowExtendSubscribeModal(false)}
                                isOnlyCouponUse={showExtendSubscribeModal.is_only_coupon_use}
                            />
                        </AbstractModal>
                    )}
                    {showPlanGroupRenameModal && (
                        <AbstractModal
                            modalTitle="Group Rename"
                            width={500}
                            height={200}
                            exitModal={(e) => setShowPlanGroupRenameModal(false)}
                        >
                            <PaymentGroupRenameModal
                                group={showPlanGroupRenameModal}
                                exitModal={(e) => setShowPlanGroupRenameModal(false)}
                            />
                        </AbstractModal>
                    )}
                    {showPlanGroupMemberModal && (
                        <AbstractModal
                            modalTitle="멤버 관리"
                            width={500}
                            height={640}
                            exitModal={(e) => setShowPlanGroupMemberModal(false)}
                        >
                            <PaymentGroupMemberModal
                                paymentPlanGroup={showPlanGroupMemberModal}
                                exitModal={(e) => setShowPlanGroupMemberModal(false)}
                            />
                        </AbstractModal>
                    )}
                </ContentsWrap>
            </BodyWithBreadcrumb>
        </LoginUserLayout>
    );
}

export default PaymentPage;
